import { Component, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { Option } from 'src/app/base/models/option.model';
import { SimpleSearchComponent } from '../../../../../app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { DateCodeInquiryService } from '../../services/datecode-inquiry.service';
import { DateCodeInquiryCondition } from '../../models/stock-summary-master.model';
import { takeUntil, Subject, switchMap, lastValueFrom, filter } from 'rxjs';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { TranslateDataValuePipe } from 'src/app/base/pipes/translate-data-value.pipe';
import { TabMenu } from 'primeng/tabmenu';
import { DateCodeInquiryLine } from '../../models/datecode-inquiry.model';
@Component({
  selector: 'app-datecode-inquiry',
  templateUrl: './datecode-inquiry.component.html',
  styleUrls: ['./datecode-inquiry.component.scss'],
  providers: [TranslateDataValuePipe],
})
export class DatecodeInquiryComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'uuid';

  override rowDataDeleteHintFieldName: string = 'uuid';

  override componentClassName: string = 'DatecodeInquiryComponent';

  // 取得 Table 分頁頁籤物件，後面用來抓頁籤資訊用的
  @ViewChild('detailTabMenuComponent')
  override detailTabMenuComponent!: TabMenu;

  override detailDatas: DateCodeInquiryLine[] = [];

  // datecode inquiry 保存原始資料
  private originalDetailDatas: DateCodeInquiryLine[] = [];

  ouFullName: string = '';
  brand: string = '';
  vendorCategory: string = '';
  itemNumber: string = '';
  dateOfInquiry: string = '';
  totalQty: number = 0;
  queryFailure: boolean = true;

  // OU AutoComplete Dropdown 的 Suggestions 資料 (篩選後的資料)
  ouSuggestions: Option[] = [];

  // Item AutoComplete Dropdown 的 Suggestions 資料 (篩選後的資料)
  itemSuggestions: Option[] = [];

  // OU AutoComplete Dropdown 值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Item AutoComplete Dropdown 值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private readonly itemSubject$ = new Subject<string | undefined>();

  override showActions: boolean = false;

  queryId: string = '';

  constructor(
    private readonly stockSummaryService: StockSummaryService,
    private readonly commonService: CommonService,
    private readonly dateCodeInquiryService: DateCodeInquiryService,
    private readonly translateDataValuePipe: TranslateDataValuePipe
  ) {
    super();
  }

  override async doQuery(requeryCondition?: any): Promise<boolean> {
    requeryCondition ??= new DateCodeInquiryCondition(
      this.formControl('ouId').getRawValue()?.value,
      this.formControl('ouId').getRawValue()?.label,
      this.formControl('itemNumber').getRawValue()?.value
    );

    const response = await lastValueFrom(
      this.dateCodeInquiryService.getDateCodeInquiry(
        requeryCondition as DateCodeInquiryCondition
      )
    );
    this.queryId = response.queryId;
    if (!response.failure && response.queryId && response.responseData) {
      const data = response.responseData;
      this.ouFullName = data.ouFullName ?? '';
      this.itemNumber = data.itemNumber ?? '';
      this.brand = data.brand ?? '';
      this.vendorCategory = data.vendorCategory ?? '';
      this.dateOfInquiry = data.dateOfInquiry ?? '';
      this.totalQty = data.totalQty ?? 0;
      this.queryFailure = response.failure ?? true;
      // 保存原始資料
      this.originalDetailDatas = data.dateCodeInquiryLine ?? [];
      // 初始顯示原始資料
      this.detailDatas = [...this.originalDetailDatas];
    } else if (response.failure) {
      this.systemMessageService.error(response.exceptionMsg);
      this.queryFailure = response.failure ?? true;
    }
    return true;
  }

  override initTableColumns(): void {
    this.detailTabs = [
      {
        id: this.parseToId('detailDataTab'),
        label: this.translateService.instant('label.dateCode-inquiry-detail'),
        automationId: 'label.dateCode-inquiry-detail',
      },
      {
        id: this.parseToId('totalQtyTab'),
        label: this.translateService.instant(
          'label.dateCode-inquiry-total-qty'
        ),
        automationId: 'label.dateCode-inquiry-total-qty',
      },
    ];
    this.detailTabMenuComponent.activeItem = this.detailTabs[0];
    this.detailTabColumns[this.parseToId('detailDataTab')] = [
      {
        header: 'field.seq',
        field: 'seq',
        sortable: true,
      },
      {
        header: 'field.whse-area-code',
        field: 'whseAreaCode',
        sortable: true,
        customField: true,
      },
      {
        header: 'field.date-code',
        field: 'dateCode',
        sortable: true,
      },
      {
        header: 'field.qty',
        field: 'qty',
        numberField: true,
        sortable: true,
      },
      {
        header: 'field.coo',
        field: 'coo',
        sortable: true,
      },
      {
        header: 'field.age-in-week',
        field: 'ageInWeek',
        sortable: true,
      },
      {
        header: 'field.vendor-version',
        field: 'vendorVersion',
        sortable: true,
      },
      {
        header: 'field.brand-version',
        field: 'brandVersion',
        sortable: true,
      },
      {
        header: 'field.item-version',
        field: 'itemVersion',
        sortable: true,
      },
      {
        header: 'field.pb',
        field: 'pb',
        sortable: true,
      },
      {
        header: 'field.pack-type',
        field: 'packType',
        sortable: true,
      },
      {
        header: 'field.pack-count',
        field: 'packCount',
        sortable: true,
      },
      {
        header: 'field.pack-qty',
        field: 'packQty',
        numberField: true,
        sortable: true,
      },
    ];

    this.detailTabColumns[this.parseToId('totalQtyTab')] = [
      {
        header: 'field.seq',
        field: 'seq',
        sortable: true,
      },
      {
        header: 'field.date-code',
        field: 'dateCode',
        sortable: true,
      },
      {
        header: 'field.total-qty',
        field: 'sumOfQty',
        numberField: true,
        sortable: true,
      },
    ];

    this.tableColumns = [
      ...this.detailTabColumns[this.parseToId('detailDataTab')],
      ...this.detailTabColumns[this.parseToId('totalQtyTab')],
    ];

    this.selectedColumns =
      this.detailTabColumns[this.parseToId('detailDataTab')];
  }

  override afterDetailTabActiveItemChange(): void {
    const activeTabId = this.detailTabMenuComponent.activeItem?.id;

    if (activeTabId === this.parseToId('totalQtyTab')) {
      // 切換到 Total Qty Tab 時，過濾重複的 dateCode
      this.detailDatas = this.filterUniqueDateCode(this.originalDetailDatas);
      this.selectedColumns =
        this.detailTabColumns[this.parseToId('totalQtyTab')];
      if (this.tableComponent) {
        this.tableComponent.first = 0;
        this.tableComponent.reset();
      }
    } else if (activeTabId === this.parseToId('detailDataTab')) {
      // 切換回 Detail Tab 時，顯示原始資料
      this.detailDatas = [...this.originalDetailDatas];
      this.selectedColumns =
        this.detailTabColumns[this.parseToId('detailDataTab')];
    }
  }

  /**
   * 過濾重複的 dateCode，只保留第一筆，並重新編號 seq
   */
  private filterUniqueDateCode(
    data: DateCodeInquiryLine[]
  ): DateCodeInquiryLine[] {
    const uniqueDateCodes = new Set<string>();
    const filteredData: DateCodeInquiryLine[] = [];

    data.forEach((item) => {
      const dateCode = item.dateCode || '';

      // 如果這個 dateCode 還沒出現過，就加入結果
      if (!uniqueDateCodes.has(dateCode)) {
        uniqueDateCodes.add(dateCode);
        filteredData.push({ ...item });
      }
    });

    // 重新編號 seq
    return filteredData.map((item, index) => ({
      ...item,
      seq: index + 1,
    }));
  }

  override initFormGroup(): void {
    // 依據初次載入的資料當作查詢條件
    this.formGroup = this.formBuilder.nonNullable.group({
      uuid: this.dynamicDialogConfig.data.uuid ?? '',
      ouId: ['', Validators.required],
      itemNumber: ['', Validators.required],
    });
  }

  override async initFormData(): Promise<any> {
    this.loadingMaskService.show();
    // 如果沒有傳入查詢條件，代表從側邊選單點擊，無須執行查詢
    if (this.dynamicDialogConfig?.data.uuid) {
      await this.doQuery(this.dynamicDialogConfig?.data);
    }
    return true;
  }

  override afterFormDataLoadSuccess(): void {
    this.loadingMaskService.hide();
  }

  /**
   * OU 的 AutoComplete
   * @param event
   */
  searchOuSuggestions(event: { query: any }): void {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => {
            return this.stockSummaryService.getOrgs(keyword);
          }),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchItemSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean), // 過濾 null、undefined、'' 等 falsy 值（如需）
          switchMap((keyword: string) => this.commonService.getItems(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => {
            this.itemSuggestions = data;
            console.log(
              'this.itemSuggestions.length =',
              this.itemSuggestions.length
            );
          },
          error: (err) => {
            console.error('getItems failed:', err);
            this.itemSuggestions = [];
          },
        });
    }
    this.itemSubject$.next(event.query);
  }

  resetItemInfo() {
    this.ouFullName = '';
    this.itemNumber = '';
    this.brand = '';
    this.vendorCategory = '';
    this.dateOfInquiry = '';
    this.queryId = '';
    this.totalQty = 0;
    this.queryFailure = true;
  }

  getSortableField(column: any): string {
    if (column.translateDataValueField) {
      return this.translateDataValuePipe.transformFieldLang(
        column.field,
        column.translateDataValueField
      );
    }
    return column.field;
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // 防止滾動頁面
    }
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  override initExtraDetailMenu(): void {
    return;
  }

  override setExtraDetailMenuStatus(): void {
    return;
  }

  override initStaticOptions(): void {
    return;
  }

  override initDialogConfig(dataAction: DataAction): void {
    return;
  }

  override initExtraRowActionMenu(): void {
    return;
  }

  override setExtraRowActionMenuStatus(): void {
    return;
  }

  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }

  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }

  override afterLangChange(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override initFormControlDisabled(): void {
    return;
  }
}
